"use client";

import { createContext, useState, useContext, useEffect } from "react";
import { getCookie, setCookie } from "cookies-next";
import translations from "@/lib/i18n";
import en from "@/locales/en";

type LanguageContextProps = {
  lang: string;
  switchLanguage: (lang: string) => void;
  t: (key: keyof typeof en) => string;
};

const LanguageContext = createContext<LanguageContextProps | undefined>(
  undefined
);

export const LanguageProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [lang, setLang] = useState<string>(() => {
    // Définit une langue par défaut côté client
    return (getCookie("lang") as string) || "fr";
  });

  const switchLanguage = (newLang: string) => {
    setCookie("lang", newLang, { path: "/" });
    setLang(newLang);
  };

  const t = (key: string): string => {
    return translations[lang]?.[key] || key;
  };

  useEffect(() => {
    const savedLang = getCookie("lang") as string;
    if (savedLang && savedLang !== lang) {
      setLang(savedLang);
    }
  }, []);

  return (
    <LanguageContext.Provider value={{ lang, switchLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
